// Paramètres → Pipeline data methods. Supabase: public.set_deal_stages / public.delete_deal_stage
// (supabase/schemas/61_gavion_deal_stages.sql §4); FakeRest (demo build, tests): the same checks and codes.
import type { SupabaseClient } from "@supabase/supabase-js";
import type { DataProvider, Identifier } from "ra-core";
import { createFakeConfigStore, isObject } from "../fakeConfigStore";
import {
  isOpenStage,
  isSystemStage,
  legacyProbabilities,
  normalizeStages,
  orderedStages,
  validateStages,
  type StageDef,
} from "../../domain/deals/stageModel";

/** A refusal: `code` = the RPC's message (stage_in_use, …), `detail` = values or rule names. */
export class PipelineError extends Error {
  constructor(
    public readonly code: string,
    public readonly detail: string = "",
  ) {
    super(code);
  }
}

export type PipelineMethods = {
  setDealStages(stages: readonly StageDef[]): Promise<StageDef[]>;
  deleteDealStage(
    value: string,
    moveTo: string | null,
  ): Promise<{ moved: number }>;
  /** Every deal at that stage, archived ones too. */
  countDealsAtStage(value: string): Promise<number>;
};

const toPipelineError = (e: { message: string; details?: string | null }) =>
  new PipelineError(e.message, e.details ?? "");

export const createSupabasePipelineMethods = (
  getClient: () => SupabaseClient,
): PipelineMethods => ({
  async setDealStages(stages) {
    const { data, error } = await getClient().rpc("set_deal_stages", {
      p_stages: stages,
    });
    if (error) throw toPipelineError(error);
    return data as StageDef[];
  },
  async deleteDealStage(value, moveTo) {
    const { data, error } = await getClient().rpc("delete_deal_stage", {
      p_value: value,
      p_move_to: moveTo,
    });
    if (error) throw toPipelineError(error);
    return data as { moved: number };
  },
  async countDealsAtStage(value) {
    const { count, error } = await getClient()
      .from("deals")
      .select("id", { count: "exact", head: true })
      .eq("stage", value);
    if (error) throw toPipelineError(error);
    return count ?? 0;
  },
});

const STAGE_FIELDS = ["to_stage", "from_stage", "deal.stage"];

/** A condition leaf on a stage field whose value is, or lists, `value` (SQL `$.** ? (@.field == … && @.value[*] == $v)`). */
const leafMentions = (node: unknown, value: string): boolean => {
  if (Array.isArray(node)) return node.some((n) => leafMentions(n, value));
  if (!isObject(node)) return false;
  if (
    typeof node.field === "string" &&
    STAGE_FIELDS.includes(node.field) &&
    (node.value === value ||
      (Array.isArray(node.value) && node.value.includes(value)))
  )
    return true;
  return Object.values(node).some((n) => leafMentions(n, value));
};

/** Mirror of private.deal_stage_rule_names: stage condition leaves, conditions.stage_in, create_deal / move_deal action stages. */
const ruleMentions = (
  r: { conditions?: unknown; actions?: unknown },
  value: string,
): boolean =>
  leafMentions(r.conditions, value) ||
  (isObject(r.conditions) &&
    Array.isArray(r.conditions.stage_in) &&
    r.conditions.stage_in.includes(value)) ||
  (Array.isArray(r.actions) &&
    r.actions.some(
      (a) =>
        isObject(a) &&
        (a.type === "create_deal" || a.type === "move_deal") &&
        a.stage === value,
    ));

export const createFakePipelineMethods = (
  dp: DataProvider,
): PipelineMethods => {
  const { list, readRow, writeKey } = createFakeConfigStore(dp);
  const current = async () => {
    const cfg = (await readRow())?.config ?? {};
    return normalizeStages(cfg.dealStages, legacyProbabilities(cfg));
  };
  const write = (stages: StageDef[]) => writeKey("dealStages", stages);
  const deals = (value: string) =>
    list<{ id: Identifier; stage: string }>("deals", { stage: value });
  // Enabled rules only, in the SQL order (position, id); a db without the collection has no rules.
  const rulesMentioning = async (value: string) =>
    (
      await list<{
        id: number;
        position?: number;
        name: string;
        enabled?: boolean;
        conditions?: unknown;
        actions?: unknown;
      }>("automation_rules", {}, true)
    )
      .filter((r) => r.enabled !== false && ruleMentions(r, value))
      .sort((x, y) => (x.position ?? 0) - (y.position ?? 0) || x.id - y.id)
      .map((r) => r.name);
  return {
    async setDealStages(stages) {
      const issue = validateStages(stages);
      if (issue) throw new PipelineError(issue.code, issue.value);
      const values = stages.map((s) => s.value);
      const removed = (await current())
        .map((s) => s.value)
        .filter((v) => !values.includes(v));
      const inUse: string[] = [];
      for (const v of removed) if ((await deals(v)).length) inUse.push(v);
      if (inUse.length)
        throw new PipelineError("stage_in_use", inUse.sort().join(", "));
      const rules = [
        ...new Set((await Promise.all(removed.map(rulesMentioning))).flat()),
      ].sort();
      if (rules.length)
        throw new PipelineError("stage_in_rules", rules.join(", "));
      const next = orderedStages(stages).map((s) => ({
        value: s.value,
        label: s.label.trim(),
        color: s.color.toLowerCase(),
        probability:
          s.value === "gagne" ? 1 : s.value === "perdu" ? 0 : s.probability,
      }));
      await write(next);
      return next;
    },
    async deleteDealStage(value, moveTo) {
      const stages = await current();
      if (isSystemStage(value)) throw new PipelineError("system_stage", value);
      if (!stages.some((s) => s.value === value))
        throw new PipelineError("unknown_stage", value);
      const rules = await rulesMentioning(value);
      if (rules.length)
        throw new PipelineError("stage_in_rules", rules.join(", "));
      const moving = await deals(value);
      if (
        moving.length &&
        (moveTo === null ||
          moveTo === value ||
          !isOpenStage(moveTo) ||
          !stages.some((s) => s.value === moveTo))
      )
        throw new PipelineError("invalid_move_target", moveTo ?? "null");
      const at = new Date().toISOString();
      for (const d of moving) {
        await dp.update("deals", {
          id: d.id,
          data: { stage: moveTo, stage_changed_at: at },
          previousData: d,
        });
        try {
          await dp.create("deal_stage_events", {
            data: {
              deal_id: d.id,
              from_stage: value,
              to_stage: moveTo,
              at,
              sales_id: null,
            },
          });
        } catch {
          // a FakeRest db without the collection: the board still moves
        }
      }
      await write(stages.filter((s) => s.value !== value));
      return { moved: moving.length };
    },
    async countDealsAtStage(value) {
      return (await deals(value)).length;
    },
  };
};
